"use client"

import { useState } from "react"
import { useTranslations } from "next-intl"
import { useToast } from "@/hooks/use-toast"
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast"
import { AlertTriangle, Check, CheckCircle2, Copy } from "lucide-react"

function CopyErrorButton({ text, label, doneLabel }: { text: string; label: string; doneLabel: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 2000)
        }).catch(() => {})
      }}
      className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs font-medium hover:bg-muted transition-colors"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? doneLabel : label}
    </button>
  )
}

/** Error-ish text worth pasting into a bug report — a plain notice ("No
 *  playable tracks") isn't (Andrés 2026-11-01: an unneeded Copy button). */
const TECHNICAL = /error|failed|failure|exception|denied|permissions?\b|timeout|timed out|network|unavailable|invalid|quota|\b(code|status)\b|\b[45]\d{2}\b|\b[a-z]+\/[a-z-]+\b|[{}]/i

export function Toaster() {
  const { toasts } = useToast()
  const t = useTranslations("common")

  return (
    // Swipe away in any direction feels natural for a centred card.
    <ToastProvider swipeDirection="up">
      {toasts.map(function ({ id, title, description, action, copyable, ...props }) {
        const isError = (props as any).variant === 'destructive'
        const copyText = [title, description].filter(v => typeof v === 'string' && v).join(' – ')
        const showCopy = isError && !!copyText && (copyable ?? TECHNICAL.test(copyText))
        const Icon = isError ? AlertTriangle : CheckCircle2
        return (
          <Toast key={id} {...props}>
            <Icon className={isError ? "mt-0.5 h-5 w-5 shrink-0 text-destructive" : "mt-0.5 h-5 w-5 shrink-0 text-primary"} aria-hidden="true" />
            <div className="grid gap-1 flex-1 min-w-0">
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && (
                <ToastDescription className="break-words">{description}</ToastDescription>
              )}
              {showCopy && <CopyErrorButton text={copyText} label={t("copy")} doneLabel={t("copied")} />}
              {action && <div className="mt-2">{action}</div>}
            </div>
            <ToastClose aria-label={t("close")} />
          </Toast>
        )
      })}
      <ToastViewport />
    </ToastProvider>
  )
}
